/**
 * Equal Credit Opportunity Act (ECOA) Form Section
 * Collects HMDA demographic information (voluntary)
 */

import { UserIdVerificationIcon } from "@porchpass/ui";
import { Separator } from "@porchpass/ui";
import * as React from "react";
import { FormTable, FormTableBody, FormTableBodyHeading } from "@porchpass/ui";
import {
  EcoaEthnicityFields,
  EcoaRaceFields,
  EcoaSexFields,
} from "@porchpass/ui";
import { useApplicationFormStore } from "../hooks/useApplicationFormStore";

interface EcoaFormProps {
  /** Whether this is for the co-borrower */
  isCoBorrower?: boolean;
}

export const EcoaForm: React.FC<EcoaFormProps> = ({ isCoBorrower = false }) => {
  const store = useApplicationFormStore();
  const borrower = isCoBorrower
    ? store.data.coBorrower
    : store.data.primaryBorrower;
  const updateBorrower = isCoBorrower
    ? store.updateCoBorrower
    : store.updatePrimaryBorrower;

  const borrowerPrefix = isCoBorrower ? "coBorrower" : "primaryBorrower";

  if (!borrower) return null;

  const biologicalSexError =
    store.fieldErrors[`${borrowerPrefix}.biologicalSex`];
  const ethnicitiesError = store.fieldErrors[`${borrowerPrefix}.ethnicities`];
  const racesError = store.fieldErrors[`${borrowerPrefix}.races`];

  const clearEthnicityError = () => {
    if (store.fieldErrors[`${borrowerPrefix}.ethnicities`]) {
      store.clearFieldError(`${borrowerPrefix}.ethnicities`);
    }
  };

  const clearRaceError = () => {
    if (store.fieldErrors[`${borrowerPrefix}.races`]) {
      store.clearFieldError(`${borrowerPrefix}.races`);
    }
  };

  const clearSexError = () => {
    if (store.fieldErrors[`${borrowerPrefix}.biologicalSex`]) {
      store.clearFieldError(`${borrowerPrefix}.biologicalSex`);
    }
  };

  return (
    <FormTable
      title="Equal Credit Opportunity Act"
      headerIcon={
        <UserIdVerificationIcon
          variant="bulk-rounded"
          size={16}
          className="text-[#CA7900]"
        />
      }
    >
      <FormTableBody>
        <div className="flex flex-col gap-4">
          <FormTableBodyHeading title="Ethnicity" className="py-0" />
          <EcoaEthnicityFields
            value={{ ethnicities: borrower.ethnicities }}
            onChange={(patch) => {
              clearEthnicityError();
              updateBorrower(patch);
            }}
            errors={{ ethnicities: ethnicitiesError }}
          />

          <Separator className="mt-2" />
          <FormTableBodyHeading title="Race" className="py-0" />
          <EcoaRaceFields
            value={{ races: borrower.races }}
            onChange={(patch) => {
              clearRaceError();
              updateBorrower(patch);
            }}
            errors={{ races: racesError }}
          />

          <Separator className="mt-2" />
          <FormTableBodyHeading title="Biological Sex" className="py-0" />
          <EcoaSexFields
            value={{ biologicalSex: borrower.biologicalSex }}
            onChange={(patch) => {
              clearSexError();
              updateBorrower(patch);
            }}
            errors={{ biologicalSex: biologicalSexError }}
          />
        </div>
      </FormTableBody>
    </FormTable>
  );
};

export default EcoaForm;
